// =============================================================================
// FILE: src/lib/subscription-gate.js
// MODULE: Billing — Subscription Gate
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   The single source of truth for what each subscription tier can do.
//   Every feature in the app that has a tier restriction calls this module.
//   Never scatter tier checks inline across components — always go through here.
//
//   This module is intentionally framework-agnostic vanilla JS so it can be
//   imported by the frontend, Edge Functions, or any future server-side code
//   without modification.
//
// USAGE EXAMPLES:
//
//   // Check if a tenant can create another tournament
//   const result = canCreateTournament(tenant);
//   if (!result.allowed) showUpgradePrompt(result.reason, result.requiredTier);
//
//   // Get the full feature set for a tier (for pricing page)
//   const features = getTierFeatures('pro');
//
//   // Check if a tenant's payment status blocks access
//   const blocked = isPaymentBlocked(tenant);
//
// PRINCIPLE:
//   When a tenant hits a limit, we NEVER delete or hide their data.
//   We show a soft "upgrade to continue" prompt and preserve everything.
//   This is Arcus's UX differentiator — other apps just lock you out.
//
// DEPENDENCIES: None (pure JS, no imports)
//
// LAST UPDATED: 2026-04 | Arcus / Claude
// =============================================================================


// =============================================================================
// TIER DEFINITIONS
// The single authoritative object for what each tier includes.
// When pricing changes, update ONLY this object — all gate functions read from it.
//
// STRUCTURE per tier:
//   maxTournaments     → max active (non-archived) tournaments at one time
//   maxTeamsPerTournament → max teams in a single tournament
//   maxPlayersPerTeam  → max players per team roster
//   maxReferees        → max referee accounts
//   sportTypes         → which sports are available (array of sport_key strings)
//   canExportData      → can download CSV/PDF reports
//   canCustomizeBranding → can upload logo, set colors
//   hasAnalytics       → access to stats dashboards and charts
//   hasPrioritySupport → email support SLA
//   priceMonthly       → USD price shown on pricing page (display only)
// =============================================================================
export const TIER_DEFINITIONS = {

  demo: {
    label:                  "Demo",
    priceMonthly:           0,
    maxTournaments:         1,
    maxTeamsPerTournament:  4,
    maxPlayersPerTeam:      10,
    maxReferees:            1,
    sportTypes:             ["soccer"],   // Demo only gets soccer to drive upgrades
    canExportData:          false,
    canCustomizeBranding:   false,
    hasAnalytics:           false,
    hasPrioritySupport:     false,
    description:            "Try Arcus with one soccer tournament. No credit card required.",
  },

  starter: {
    label:                  "Starter",
    priceMonthly:           29,
    maxTournaments:         3,
    maxTeamsPerTournament:  8,
    maxPlayersPerTeam:      20,
    maxReferees:            3,
    sportTypes:             ["soccer", "volleyball"],
    canExportData:          false,
    canCustomizeBranding:   false,
    hasAnalytics:           false,
    hasPrioritySupport:     false,
    description:            "Perfect for a single sport organization running a few leagues.",
  },

  pro: {
    label:                  "Pro",
    priceMonthly:           59,
    maxTournaments:         Infinity,    // Unlimited
    maxTeamsPerTournament:  32,
    maxPlayersPerTeam:      30,
    maxReferees:            10,
    sportTypes:             ["soccer", "volleyball", "basketball", "baseball", "rugby", "flag_football"],
    canExportData:          true,
    canCustomizeBranding:   true,
    hasAnalytics:           true,
    hasPrioritySupport:     false,
    description:            "All sports, unlimited tournaments, full analytics. Most popular.",
  },

  club: {
    label:                  "Club",
    priceMonthly:           99,
    maxTournaments:         Infinity,
    maxTeamsPerTournament:  Infinity,
    maxPlayersPerTeam:      Infinity,
    maxReferees:            Infinity,
    sportTypes:             ["soccer", "volleyball", "basketball", "baseball", "rugby", "flag_football"],
    canExportData:          true,
    canCustomizeBranding:   true,
    hasAnalytics:           true,
    hasPrioritySupport:     true,
    description:            "Everything in Pro plus white-label branding and priority support.",
  },
};

// Ordered array of tier keys for comparison logic (lower index = lower tier)
const TIER_ORDER = ["demo", "starter", "pro", "club"];


// =============================================================================
// GATE FUNCTIONS
// Each function checks one specific action against a tenant's current tier.
//
// RETURN SHAPE (all gate functions return this):
// {
//   allowed: boolean,       — whether the action is permitted right now
//   reason: string,         — human-readable explanation (shown in upgrade prompt)
//   requiredTier: string|null — the minimum tier that would allow this (null if allowed)
// }
// =============================================================================

// ---------------------------------------------------------------------------
// GATE: canCreateTournament
// Checks if the tenant can create one more tournament.
// Uses the denormalized active_tournament_count from the tenant row
// to avoid a COUNT() query on every check.
//
// @param {object} tenant - Row from the tenants table
//   Required fields: subscription_tier, subscription_status, active_tournament_count
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canCreateTournament(tenant) {
  // Payment issues block all write operations
  const paymentBlock = isPaymentBlocked(tenant);
  if (paymentBlock) return paymentBlock;

  const limits = TIER_DEFINITIONS[tenant.subscription_tier];
  const currentCount = tenant.active_tournament_count ?? 0;

  if (currentCount >= limits.maxTournaments) {
    const requiredTier = getNextTierWithHigherLimit(tenant.subscription_tier, "maxTournaments");
    return {
      allowed: false,
      reason: `Your ${limits.label} plan includes up to ${limits.maxTournaments} active tournament${limits.maxTournaments === 1 ? "" : "s"}. Archive an existing tournament or upgrade to create more.`,
      requiredTier,
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// ---------------------------------------------------------------------------
// GATE: canUseSport
// Checks if the tenant's tier includes a specific sport type.
// Called when an admin selects a sport during tournament creation.
//
// @param {object} tenant - Row from the tenants table
// @param {string} sportKey - e.g. "rugby", "basketball"
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canUseSport(tenant, sportKey) {
  const paymentBlock = isPaymentBlocked(tenant);
  if (paymentBlock) return paymentBlock;

  const limits = TIER_DEFINITIONS[tenant.subscription_tier];

  if (!limits.sportTypes.includes(sportKey)) {
    // Find the lowest tier that includes this sport
    const requiredTier = TIER_ORDER.find((tier) =>
      TIER_DEFINITIONS[tier].sportTypes.includes(sportKey)
    ) ?? "pro";

    return {
      allowed: false,
      reason: `${sportKey.replace("_", " ")} is not available on the ${limits.label} plan. Upgrade to ${TIER_DEFINITIONS[requiredTier].label} to unlock all sports.`,
      requiredTier,
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// ---------------------------------------------------------------------------
// GATE: canAddTeamToTournament
// Checks if a tournament has room for one more team.
// currentTeamCount must be fetched by the caller before calling this.
//
// @param {object} tenant - Row from the tenants table
// @param {number} currentTeamCount - How many teams are already in the tournament
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canAddTeamToTournament(tenant, currentTeamCount) {
  const paymentBlock = isPaymentBlocked(tenant);
  if (paymentBlock) return paymentBlock;

  const limits = TIER_DEFINITIONS[tenant.subscription_tier];

  if (currentTeamCount >= limits.maxTeamsPerTournament) {
    const requiredTier = getNextTierWithHigherLimit(tenant.subscription_tier, "maxTeamsPerTournament");
    return {
      allowed: false,
      reason: `Your ${limits.label} plan supports up to ${limits.maxTeamsPerTournament} teams per tournament. Upgrade to add more teams.`,
      requiredTier,
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// ---------------------------------------------------------------------------
// GATE: canAddPlayerToTeam
// Checks if a team has room for one more player.
//
// @param {object} tenant - Row from the tenants table
// @param {number} currentPlayerCount - How many players are already on the team
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canAddPlayerToTeam(tenant, currentPlayerCount) {
  const paymentBlock = isPaymentBlocked(tenant);
  if (paymentBlock) return paymentBlock;

  const limits = TIER_DEFINITIONS[tenant.subscription_tier];

  if (currentPlayerCount >= limits.maxPlayersPerTeam) {
    const requiredTier = getNextTierWithHigherLimit(tenant.subscription_tier, "maxPlayersPerTeam");
    return {
      allowed: false,
      reason: `Your ${limits.label} plan supports up to ${limits.maxPlayersPerTeam} players per team. Upgrade to expand your rosters.`,
      requiredTier,
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// ---------------------------------------------------------------------------
// GATE: canExportData
// Checks if the tenant can download reports and exports.
//
// @param {object} tenant - Row from the tenants table
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canExportData(tenant) {
  const paymentBlock = isPaymentBlocked(tenant);
  if (paymentBlock) return paymentBlock;

  const limits = TIER_DEFINITIONS[tenant.subscription_tier];

  if (!limits.canExportData) {
    return {
      allowed: false,
      reason: "Data export is available on Pro and Club plans. Upgrade to download reports.",
      requiredTier: "pro",
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// ---------------------------------------------------------------------------
// GATE: canAccessAnalytics
// Checks if the tenant can view the analytics dashboard.
//
// @param {object} tenant - Row from the tenants table
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canAccessAnalytics(tenant) {
  const paymentBlock = isPaymentBlocked(tenant);
  if (paymentBlock) return paymentBlock;

  const limits = TIER_DEFINITIONS[tenant.subscription_tier];

  if (!limits.hasAnalytics) {
    return {
      allowed: false,
      reason: "Analytics are available on Pro and Club plans.",
      requiredTier: "pro",
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// ---------------------------------------------------------------------------
// GATE: canCustomizeBranding
// Checks if the tenant can upload logos and set brand colors.
//
// @param {object} tenant - Row from the tenants table
// @returns {GateResult}
// ---------------------------------------------------------------------------
export function canCustomizeBranding(tenant) {
  const limits = TIER_DEFINITIONS[tenant.subscription_tier];

  if (!limits.canCustomizeBranding) {
    return {
      allowed: false,
      reason: "Custom branding is available on Pro and Club plans.",
      requiredTier: "pro",
    };
  }

  return { allowed: true, reason: null, requiredTier: null };
}


// =============================================================================
// PAYMENT STATUS HELPERS
// Separate from tier checks — a tenant can be on Pro but have a failed payment.
// Payment status checks should run BEFORE tier limit checks.
// =============================================================================

// ---------------------------------------------------------------------------
// HELPER: isPaymentBlocked
// Returns a GateResult if the tenant's payment status should block write access.
// Returns null if payment is fine (so callers can use: const block = isPaymentBlocked(t); if (block) return block;)
//
// POLICY:
//   active   → no block
//   trialing → no block (they're within their trial)
//   past_due → soft block on NEW creates only; existing data still accessible
//   unpaid   → hard block on all write operations
//   canceled → hard block; show re-subscribe prompt
//
// @param {object} tenant - Row from the tenants table
// @returns {GateResult|null}
// ---------------------------------------------------------------------------
export function isPaymentBlocked(tenant) {
  const status = tenant.subscription_status;

  if (status === "active" || status === "trialing") {
    return null; // No block
  }

  if (status === "past_due") {
    return {
      allowed: false,
      reason: "There's an issue with your payment method. Your data is safe — update your billing info to continue.",
      requiredTier: null,       // Not a tier issue — redirect to billing, not upgrade
      isBillingIssue: true,     // Frontend uses this to show billing CTA instead of upgrade CTA
    };
  }

  if (status === "canceled" || status === "unpaid") {
    return {
      allowed: false,
      reason: "Your subscription has ended. Your tournaments and data are preserved — reactivate your plan to continue.",
      requiredTier: "starter",  // Minimum tier to reactivate
      isBillingIssue: true,
    };
  }

  return null; // Unknown status — don't block, log separately
}


// =============================================================================
// INFORMATIONAL HELPERS
// Used by pricing pages, upgrade modals, and feature hints.
// These do NOT block — they return display data only.
// =============================================================================

// ---------------------------------------------------------------------------
// HELPER: getTierFeatures
// Returns the full feature definition for a given tier.
// Used to render the pricing page and upgrade modal feature lists.
//
// @param {string} tier - One of: demo, starter, pro, club
// @returns {object} - The tier definition object from TIER_DEFINITIONS
// ---------------------------------------------------------------------------
export function getTierFeatures(tier) {
  return TIER_DEFINITIONS[tier] ?? TIER_DEFINITIONS.demo;
}


// ---------------------------------------------------------------------------
// HELPER: getAllTiers
// Returns all tiers in order (demo → club), useful for rendering pricing tables.
//
// @returns {Array<{key: string, ...tierDefinition}>}
// ---------------------------------------------------------------------------
export function getAllTiers() {
  return TIER_ORDER.map((key) => ({
    key,
    ...TIER_DEFINITIONS[key],
  }));
}


// ---------------------------------------------------------------------------
// HELPER: isHigherTier
// Returns true if tierA is a higher tier than tierB.
// Used to determine if an action is an upgrade or downgrade.
//
// @param {string} tierA
// @param {string} tierB
// @returns {boolean}
// ---------------------------------------------------------------------------
export function isHigherTier(tierA, tierB) {
  return TIER_ORDER.indexOf(tierA) > TIER_ORDER.indexOf(tierB);
}


// ---------------------------------------------------------------------------
// HELPER: getUpgradeMessage
// Returns a contextual upgrade message for a given gate result.
// Used in the upgrade prompt modal to show the right CTA copy.
//
// @param {GateResult} gateResult - Result from any gate function
// @param {string} currentTier - The tenant's current tier
// @returns {object} { headline, body, ctaLabel }
// ---------------------------------------------------------------------------
export function getUpgradeMessage(gateResult, currentTier) {
  if (gateResult.isBillingIssue) {
    return {
      headline: "Payment issue",
      body: gateResult.reason,
      ctaLabel: "Update billing",
    };
  }

  const requiredTierDef = TIER_DEFINITIONS[gateResult.requiredTier];
  const currentTierDef  = TIER_DEFINITIONS[currentTier];

  return {
    headline: `Upgrade to ${requiredTierDef?.label ?? "a higher plan"}`,
    body: gateResult.reason,
    ctaLabel: `Upgrade from ${currentTierDef?.label} → ${requiredTierDef?.label} ($${requiredTierDef?.priceMonthly}/mo)`,
  };
}


// =============================================================================
// INTERNAL UTILITIES
// Not exported — used only within this module.
// =============================================================================

// ---------------------------------------------------------------------------
// INTERNAL: getNextTierWithHigherLimit
// Finds the cheapest tier that has a higher value for a specific limit field.
// Used to tell users exactly which tier they need, not just "upgrade."
//
// @param {string} currentTier
// @param {string} limitField - Key in TIER_DEFINITIONS (e.g. "maxTournaments")
// @returns {string} - The tier key of the cheapest upgrade that resolves the limit
// ---------------------------------------------------------------------------
function getNextTierWithHigherLimit(currentTier, limitField) {
  const currentValue = TIER_DEFINITIONS[currentTier][limitField];
  const currentIndex = TIER_ORDER.indexOf(currentTier);

  // Walk up from the current tier and return the first one with a higher limit
  for (let i = currentIndex + 1; i < TIER_ORDER.length; i++) {
    const candidate = TIER_ORDER[i];
    if (TIER_DEFINITIONS[candidate][limitField] > currentValue) {
      return candidate;
    }
  }

  // Already at the highest tier — shouldn't happen in practice
  return "club";
}
